import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useAtom } from "jotai";
import { themeAtom, isMobileAtom } from "../-Atoms";

dayjs.extend(utc);
dayjs.extend(timezone);

const ScheduleEmailModal = ({
  open,
  onClose,
  onSchedule,
  isLoading = false,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isMobile] = useAtom(isMobileAtom);
  
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [error, setError] = useState("");

  const isDark = theme === "dark";
  
  const themeStyles = {
    background: isDark ? "#1e1e1e" : "#ffffff",
    color: isDark ? "#ffffff" : "#000000",
    borderColor: isDark ? "#2c2c2e" : "#d1d1d6",
    headerBg: isDark ? "#1c1c1e" : "#f2f2f7",
  };

  // Initialize with current time + 1 hour, rounded to nearest 5 minutes
  useEffect(() => {
    if (open) {
      const now = dayjs().tz("Australia/Sydney");
      const oneHourFromNow = now.add(1, "hour");
      
      // Round to nearest 5 minutes
      const minutes = oneHourFromNow.minute();
      const roundedMinutes = Math.ceil(minutes / 5) * 5;
      const roundedTime = oneHourFromNow.minute(roundedMinutes).second(0).millisecond(0);
      
      setSelectedDateTime(roundedTime);
      setError("");
    }
  }, [open]);

  const validateDateTime = (dateTime) => {
    if (!dateTime) {
      return "Please select a date and time";
    }

    const now = dayjs().tz("Australia/Sydney");
    const selected = dayjs(dateTime).tz("Australia/Sydney");

    if (selected.isBefore(now.add(1, "minute"))) {
      return "Scheduled time must be at least 1 minute in the future";
    }

    if (selected.isAfter(now.add(1, "year"))) {
      return "Scheduled time cannot be more than 1 year in the future";
    }

    return "";
  };

  const handleDateTimeChange = (newValue) => {
    setSelectedDateTime(newValue);
    if (newValue) {
      const validationError = validateDateTime(newValue);
      setError(validationError);
    }
  };

  const handleSchedule = () => {
    if (!selectedDateTime) {
      setError("Please select a date and time");
      return;
    }

    const validationError = validateDateTime(selectedDateTime);
    if (validationError) {
      setError(validationError);
      return;
    }

    onSchedule(selectedDateTime.toISOString());
  };

  const handleCancel = () => {
    setSelectedDateTime(null);
    setError("");
    onClose();
  };

  const formatScheduledTime = () => {
    if (!selectedDateTime) return "";
    
    const formatted = selectedDateTime.tz("Australia/Sydney");
    return formatted.format("dddd, D MMMM YYYY [at] h:mm A");
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? null : handleCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            borderRadius: isMobile ? 0 : "20px",
            bgcolor: themeStyles.background,
            color: themeStyles.color,
            minHeight: isMobile ? "100vh" : "auto",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: themeStyles.headerBg,
          color: themeStyles.color,
          borderBottom: `1px solid ${themeStyles.borderColor}`,
          fontSize: "1.25rem",
          fontWeight: 600,
        }}
      >
        Schedule Email
      </DialogTitle>
      
      <DialogContent
        sx={{
          bgcolor: themeStyles.background,
          color: themeStyles.color,
          padding: 3,
        }}
      >
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: themeStyles.color }}>
            Choose when you want this email to be sent:
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Scheduled Send Time"
              value={selectedDateTime}
              onChange={handleDateTimeChange}
              disabled={isLoading}
              minDateTime={dayjs().tz("Australia/Sydney").add(1, "minute")}
              maxDateTime={dayjs().tz("Australia/Sydney").add(1, "year")}
              format="DD/MM/YYYY h:mm A"
              sx={{
                width: "100%",
                mb: 2,
                "& .MuiInputLabel-root": {
                  color: isDark ? "#ffffff" : "#000000",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: themeStyles.borderColor,
                  },
                  "&:hover fieldset": {
                    borderColor: isDark ? "#ffffff" : "#000000",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#007AFF",
                  },
                  "& input": {
                    color: themeStyles.color,
                  },
                },
                "& .MuiSvgIcon-root": {
                  color: themeStyles.color,
                },
              }}
            />
          </LocalizationProvider>

          {selectedDateTime && !error && (
            <Box
              sx={{
                p: 2,
                bgcolor: isDark ? "rgba(0, 122, 255, 0.1)" : "rgba(0, 122, 255, 0.05)",
                borderRadius: 2,
                border: `1px solid ${isDark ? "rgba(0, 122, 255, 0.3)" : "rgba(0, 122, 255, 0.2)"}`,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#007AFF" }}>
                Email will be sent on:
              </Typography>
              <Typography variant="body2" sx={{ color: themeStyles.color }}>
                {formatScheduledTime()}
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" sx={{ color: isDark ? "#888" : "#666", mt: 2 }}>
            All times are in Australian Eastern Time (AEDT/AEST). The email will be processed 
            every 5 minutes, so it may be sent up to 5 minutes after the scheduled time.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: themeStyles.background,
          borderTop: `1px solid ${themeStyles.borderColor}`,
          padding: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={handleCancel}
          disabled={isLoading}
          sx={{
            color: themeStyles.color,
            borderColor: themeStyles.borderColor,
            "&:hover": {
              borderColor: themeStyles.color,
              bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
            },
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleSchedule}
          disabled={isLoading || !!error || !selectedDateTime}
          variant="contained"
          sx={{
            bgcolor: "#007AFF",
            color: "white",
            "&:hover": {
              bgcolor: "#0056CC",
            },
            "&:disabled": {
              bgcolor: isDark ? "#3a3a3c" : "#e5e5ea",
              color: isDark ? "#6d6d70" : "#c7c7cc",
            },
          }}
        >
          {isLoading ? "Scheduling..." : "Schedule Email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleEmailModal;